import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Stack, Typography } from '@mui/material'
import React from 'react'
import { BoostConfig, MyPvIo, WeekDays } from 'src/Libs/flow/installation/forms'
import { getTimeAsDate } from 'src/Libs/flow/installation/utils'
import { getUuid } from 'src/Libs/flow/utils'
import { MAX_MY_PV_TEMPERATURE_CELSIUS } from 'src/Libs/flow/advanced-settings/constants'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useDialogActions } from 'src/Libs/flow/advanced-settings/components/Dialogs/hooks'
import { FormProvider, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { getDisabledSunModeDays } from 'src/Libs/flow/advanced-settings/utils'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { isValid as isValidDate } from '@e3dc-react/shell/Libs/date'
import { TFunction } from 'i18next'
import { BoilerScheduleEntry } from './BoilerScheduleEntry'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

//#region Dialog
export interface BoilerScheduleEditorDialogProps {
  installationId: number
  myPv: MyPvIo
  onClose?: () => void
  saveBoostConfig: (newBoostConfig: BoostConfig) => void
}

/** Dialog to change the weekly charging schedule of a MyPV heater. */
export const BoilerScheduleEditorDialog: React.FC<BoilerScheduleEditorDialogProps> = ({ installationId, myPv, onClose, saveBoostConfig }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const methods = useForm({
    values: { boostConfig: myPv.boostConfig },
    mode: 'onChange',
    resolver: zodResolver(validateEditorDialogSchema(t)),
  })

  const { control, handleSubmit, reset, setError, clearErrors, formState } = methods
  const { isDirty } = formState
  const canSubmit = Object.keys(formState.errors).length === 0
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'boostConfig',
  })
  const boostConfigs = useWatch({ control, name: 'boostConfig' })

  const { disabledDays, areAllDaysSelected } = getDisabledSunModeDays(boostConfigs)

  const handleOnClose = (): void => {
    onClose?.()
  }

  const addSchedule = (): void => {
    append({
      id: getUuid(),
      fromTime: getTimeAsDate('06:00'),
      toTime: getTimeAsDate('17:00'),
      securityTemperature: 0,
      weekDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
    })
  }

  const onSubmit: SubmitHandler<{ boostConfig: BoostConfig }> = data => {
    saveBoostConfig(data.boostConfig)
    handleOnClose()
  }

  const dialogActions = useDialogActions({
    primary: {
      name: t('common.save'),
      onClick: handleSubmit(onSubmit),
      disabled: !userCanEdit || !canSubmit,
    },
    secondary: {
      onClick: handleOnClose,
    },
  })

  const additionalActionButton = (
    <Button variant="contained" sx={{ minWidth: '100%' }} onClick={() => reset()} disabled={!isDirty}>
      {t('common.reset')}
    </Button>
  )

  return (
    <Dialog
      title={t('user-settings.boiler-page.calendar-editor.title')}
      maxWidth="md"
      actions={dialogActions}
      additionalActionsLeft={additionalActionButton}
    >
      <FormProvider {...methods}>
        <Stack sx={{ gap: 2 }}>
          {fields.map((field, index) => (
            <BoilerScheduleEntry
              key={field.id}
              index={index}
              control={control}
              disabledDays={disabledDays[index]}
              onRemove={() => remove(index)}
              setError={setError}
              clearErrors={clearErrors}
              disableDeleteButton={fields.length <= 1}
            />
          ))}
          <Box>
            <Button variant="outlined" disabled={areAllDaysSelected || !userCanEdit} onClick={addSchedule}>
              {`+ ${t('common.add')}`}
            </Button>
            {areAllDaysSelected && (
              <Typography variant="caption" sx={{ ml: 3 }}>
                {t('user-settings.charging-priority-page.schedule.all-days-used')}
              </Typography>
            )}
          </Box>
        </Stack>
      </FormProvider>
    </Dialog>
  )
}
//#endregion

//#region Validation schema
const validateEditorDialogSchema = (t: TFunction): z.Schema => {
  const scheduleSchema = z
    .object({
      id: z.string(),
      securityTemperature: z.number().max(MAX_MY_PV_TEMPERATURE_CELSIUS).min(0),
      fromTime: z.date().refine(val => val === null || isValidDate(val)),
      toTime: z.date().refine(val => val === null || isValidDate(val)),
      get weekDays(): z.Schema<WeekDays> {
        return z.object({
          monday: z.boolean(),
          tuesday: z.boolean(),
          wednesday: z.boolean(),
          thursday: z.boolean(),
          friday: z.boolean(),
          saturday: z.boolean(),
          sunday: z.boolean(),
        })
      },
    })
    .refine(data => data.fromTime < data.toTime, {
      message: t('user-settings.boiler-page.calendar-editor.time-order-error'),
      path: ['toTime'],
    })
  return z.object({
    boostConfig: z.array(scheduleSchema),
  })
}
//#endregion
